import { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Trash2, ShoppingCart, ArrowRight } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

export default function Cart() {
  const navigate = useNavigate();
  const { isAuthenticated, isMember, isBuyer, loading: authLoading } = useAuth();
  const { cart, loading, removeFromCart, checkout } = useCart();

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [authLoading, isAuthenticated, navigate]);

  if (authLoading || !isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-[#71717A]">Loading...</div>
      </div>
    );
  }

  const handleCheckout = async () => {
    try {
      await checkout();
    } catch {
      toast.error('Checkout failed. Please try again.');
    }
  };

  const needsMembership = isBuyer && !isMember;

  return (
    <div className="min-h-screen py-8 lg:py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-12">
          <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-4">Your</p>
          <h1 className="font-serif text-3xl sm:text-4xl font-light tracking-tight text-[#F5F5F0]">
            Shopping Cart
          </h1>
        </div>

        {cart.items.length === 0 ? (
          <div className="text-center py-24">
            <ShoppingCart className="mx-auto text-[#71717A] mb-6" size={48} />
            <p className="text-[#71717A] text-lg mb-8">Your cart is empty</p>
            <Link to="/gallery" className="btn-primary inline-flex items-center gap-2">
              Explore Gallery
              <ArrowRight size={18} />
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Items */}
            <div className="lg:col-span-2 space-y-4">
              {cart.items.map(item => (
                <div
                  key={item.artwork.id}
                  className="flex gap-4 p-4 bg-[#121212] border border-white/5"
                  data-testid={`cart-item-${item.artwork.id}`}
                >
                  <img
                    src={item.artwork.thumbnail_url || item.artwork.image_url}
                    alt={item.artwork.title}
                    className="w-24 h-24 object-cover"
                  />
                  <div className="flex-1">
                    <h3 className="font-serif text-lg text-[#F5F5F0]">
                      {item.artwork.title}
                    </h3>
                    <p className="text-[#71717A] text-sm">
                      by {item.artwork.artist_name}
                    </p>
                    <p className="text-[#D4AF37] mt-2">
                      ${item.artwork.price?.toFixed(2)}
                    </p>
                  </div>
                  <button
                    onClick={() => removeFromCart(item.artwork.id)}
                    className="text-[#71717A] hover:text-[#EF4444] transition-colors"
                    data-testid={`remove-${item.artwork.id}`}
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              ))}
            </div>

            {/* Summary */}
            <div className="lg:col-span-1">
              <div className="p-6 bg-[#121212] border border-white/10 sticky top-24">
                <h2 className="font-serif text-xl text-[#F5F5F0] mb-6">Order Summary</h2>
                
                <div className="space-y-3 mb-6">
                  <div className="flex justify-between text-[#A1A1AA]">
                    <span>Subtotal</span>
                    <span>${cart.total?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-[#71717A] text-sm">
                    <span>Platform fee</span>
                    <span>Included</span>
                  </div>
                </div>
                
                <div className="pt-4 border-t border-white/10">
                  <div className="flex justify-between text-lg">
                    <span className="text-[#F5F5F0]">Total</span>
                    <span className="text-[#D4AF37] font-serif text-2xl">
                      ${cart.total?.toFixed(2)}
                    </span>
                  </div>
                </div>

                {needsMembership ? (
                  <div className="mt-6">
                    <p className="text-[#EF4444] text-sm mb-4">
                      Buyer membership required to purchase
                    </p>
                    <Link
                      to="/membership"
                      className="btn-primary w-full text-center block"
                    >
                      Get Membership - $5/mo
                    </Link>
                  </div>
                ) : (
                  <Button
                    onClick={handleCheckout}
                    disabled={loading}
                    className="btn-primary w-full mt-6"
                    data-testid="checkout-btn"
                  >
                    {loading ? 'Processing...' : 'Proceed to Checkout'}
                  </Button>
                )}
                <p className="text-[#71717A] text-xs mt-4 leading-relaxed">
                  By purchasing, you agree that all artworks are &copy; Scott McGuire / mortalworks.art. 
                  Full-resolution files are for personal use only. Unauthorized redistribution is prohibited.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
